import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Skeleton, Tooltip } from 'antd';
import { Link } from 'react-router-dom';

import { RecordTitle } from '../common/RecordBadge';
import { pinAccessibleName, pinBadge, pinPath, newestEmptyText, tileKicker, tileLabel } from './homePins';
import { tileFields } from './recordTileValues';

// How long a press must last to show a tile's whole values instead of opening it.
export const LONG_PRESS_MS = 500;
// What a tile says when Home could not read its record this time.
export const TILE_UNAVAILABLE_TEXT = 'Could not read this just now. Reload Home to try again.';

/**
 * "Hold to see it all" for a tile on a touch screen (KPR-1127): a press held
 * LONG_PRESS_MS opens the tile's values in full (`open`), and the tap that
 * ends it does not open the record. A move (a scroll) cancels the press. The
 * next touch anywhere closes it again.
 */
function useLongPress() {
	const [open, setOpen] = useState(false);
	const timer = useRef(null);
	const fired = useRef(false);
	const cancel = useCallback(() => {
		if (timer.current) clearTimeout(timer.current);
		timer.current = null;
	}, []);
	useEffect(() => cancel, [cancel]);
	useEffect(() => {
		if (!open) return undefined;
		const close = () => setOpen(false);
		// Attached after the press that opened it has ended.
		const handle = setTimeout(() => {
			document.addEventListener('touchstart', close, { once: true });
			document.addEventListener('mousedown', close, { once: true });
		}, 0);
		return () => {
			clearTimeout(handle);
			document.removeEventListener('touchstart', close);
			document.removeEventListener('mousedown', close);
		};
	}, [open]);
	const handlers = {
		onTouchStart: () => {
			fired.current = false;
			cancel();
			timer.current = setTimeout(() => {
				fired.current = true;
				setOpen(true);
			}, LONG_PRESS_MS);
		},
		onTouchMove: cancel,
		onTouchEnd: cancel,
		onTouchCancel: cancel,
		// Android opens a context menu on a long press; the values are the menu.
		onContextMenu: (event) => { if (fired.current) event.preventDefault(); },
		onClickCapture: (event) => {
			if (!fired.current) return;
			fired.current = false;
			event.preventDefault();
			event.stopPropagation();
		},
	};
	return { open, handlers };
}

/** One value: its element's label over it, one line, the whole value on hover. */
function TileField({ field }) {
	const full = `${field.label}: ${field.text || 'empty'}`;
	return (
		<Tooltip title={full} placement="top" overlayClassName="home-tile__tooltip">
			<span className="home-tile__field" data-testid="home-tile-field" data-element={field.name}>
				<span className="micro-label home-tile__field-label">{field.label}</span>
				<span className={`home-tile__value${field.text ? '' : ' home-tile__value--empty'}`}>
					{field.dot ? <span className="item-element__value-dot" style={{ backgroundColor: field.dot }} /> : null}
					<span className="home-tile__value-text">
						{field.pending ? <Skeleton.Input active size="small" className="home-tile__value-skeleton" /> : field.display}
					</span>
				</span>
			</span>
		</Tooltip>
	);
}

/**
 * A record tile on Home's Pinned strip (KPR-1127): a cell two columns wide
 * that shows one to three values of one record — a fixed record, or the
 * newest record of a card as Home read it when it loaded. The whole tile is
 * the door to that record (the item's detail, where it lives now). Its head
 * is the record as its own surface (RecordTitle: the card's tile, the card or
 * filter over the record's title, or "Newest <card>"); under it, each value
 * the person may read, formatted as the item's detail formats it, cut to
 * one line — the whole value on hover, or on a long press on a touch screen.
 * A newest tile with nothing matching yet says so in one line.
 *
 * `defs` are the item's card's elements (null while the card is loading),
 * `context` what the values format with (recordTileValues
 * useTileValueContext); `handle` the reorder grip, `menu` its ⋯.
 */
export default function RecordTile({ pin, defs, context, handle = null, menu = null }) {
	const { open, handlers } = useLongPress();
	const fields = pin?.item ? tileFields(pin, defs, context) : [];
	const path = pinPath(pin);
	const badge = pinBadge(pin);
	const valuesSaid = fields.map(field => `${field.label} ${field.text || 'empty'}`).join(', ');
	const body = (
		<>
			<RecordTitle
				kind={badge.kind}
				record={badge.record}
				kicker={tileKicker(pin)}
				title={tileLabel(pin)}
				size="xs"
				className="home-tile__head"
			/>
			{pin?.item ? (
				<span className="home-tile__fields" style={{ '--home-tile-fields': Math.max(1, fields.length) }}>
					{fields.map(field => <TileField key={field.name} field={field} />)}
				</span>
			) : (
				<span className="home-tile__none">
					{/* A tile Home could not read this time (KPR-1127): the rest of Home is there. */}
					{pin?.unavailable ? TILE_UNAVAILABLE_TEXT : newestEmptyText(pin)}
				</span>
			)}
		</>
	);
	const whole = fields.length ? (
		<span className="home-tile__whole">
			{fields.map(field => (
				<span key={field.name} className="home-tile__whole-row">
					<strong>{field.label}</strong> {field.text || 'empty'}
				</span>
			))}
		</span>
	) : null;
	return (
		<div className="home-tile" data-pin-kind={pin?.targetType} data-tile-source={pin?.source}>
			{handle}
			<Tooltip title={whole} open={Boolean(whole) && open} placement="bottom" overlayClassName="home-tile__tooltip">
				{path ? (
					<Link
						to={path}
						className="home-tile__body"
						aria-label={`${pinAccessibleName(pin)}${valuesSaid ? `: ${valuesSaid}` : ''}`}
						{...handlers}
					>
						{body}
					</Link>
				) : (
					<div className="home-tile__body home-tile__body--inert" role="group" aria-label={pinAccessibleName(pin)}>
						{body}
					</div>
				)}
			</Tooltip>
			{menu}
		</div>
	);
}
